import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronRight, Bot, Building2, ClipboardCheck, ClipboardList, FileStack, FileText, Database, LayoutDashboard, LucideIcon, Presentation, ScrollText, Send, ShieldAlert, Wallet, LogOut,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/* 메뉴 — 워크플로우 순서를 따른다.
 * 자금원이 먼저다. 통장이 없으면 투자를 적을 곳이 없다.
 *
 * 2026-10-03 회의로 factsheet 와 같은 범위가 됐다 — 보고 넷 · 보육 · 문서함이 붙었다.
 * 보육은 2026-11-02 걷어냈다(#102) — 투자처를 직접 고르고 공개 모집을 하지 않는다. */
type Item = { key: string; path: string; label: string; icon: LucideIcon };
type Group = { group: string; items: Item[] };

const MENU: (Item | Group)[] = [
  { key: 'dash', path: '/dashboard', label: '대시보드', icon: LayoutDashboard },
  {
    group: '투자',
    items: [
      { key: 'vehicles', path: '/vehicles', label: '자금원', icon: Wallet },
      { key: 'companies', path: '/companies', label: '투자처', icon: Building2 },
      { key: 'ledger', path: '/ledger', label: '원장', icon: ScrollText },
    ],
  },
  {
    group: '보고',
    items: [
      { key: 'periodic', path: '/reports/periodic', label: '정기보고', icon: ClipboardList },
      { key: 'risk', path: '/reports/risk', label: '리스크 평가', icon: ShieldAlert },
      { key: 'vics', path: '/reports/vics', label: 'VICS 보고', icon: Send },
      { key: 'lp', path: '/reports/lp', label: 'LP 보고', icon: FileText },
      { key: 'closing', path: '/reports/closing', label: '결산', icon: ClipboardCheck },
      { key: 'internal', path: '/reports/internal', label: '내부 보고', icon: Presentation },
    ],
  },
  {
    group: '운영',
    items: [
      { key: 'documents', path: '/documents', label: '문서함', icon: FileStack },
      { key: 'data', path: '/data', label: '데이터 내보내기', icon: Database },
      { key: 'ai', path: '/ai', label: 'AI 관리', icon: Bot },
    ],
  },
];

/* 메뉴 한 줄 — 펼침은 아이콘 + 이름, 접힘은 아이콘만(이름은 마우스를 올리면). GRK_admin 사이드바와 같은 모양 */
const Row: React.FC<{ m: Item; on: boolean; wide: boolean }> = ({ m, on, wide }) => (
  <Link to={m.path} aria-current={on ? 'page' : undefined} title={wide ? undefined : m.label}
    className={`${wide ? 'flex items-center gap-2.5 px-2.5 py-2' : 'h-10 w-10 flex items-center justify-center'} rounded-lg text-sm transition-colors ${
      on ? 'bg-indigo-50 text-indigo-600 font-medium'
         : 'text-slate-500 font-medium hover:bg-slate-50 hover:text-slate-700'
    }`}>
    <m.icon size={wide ? 16 : 20} aria-hidden="true" />
    {wide && <span className="flex-1 whitespace-nowrap">{m.label}</span>}
  </Link>
);

export const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const on = (m: Item) => pathname.startsWith(m.path);
  const { user, logout } = useAuth();
  // 사이드바 펼침 · 메뉴 묶음 펼침 — GRK_admin 과 같다(처음엔 다 펼침, 저장하지 않음)
  const [wide, setWide] = useState(true);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const groupOpen = (g: string) => openGroups[g] !== false;
  const toggleGroup = (g: string) => setOpenGroups((x) => ({ ...x, [g]: !groupOpen(g) }));
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  // 바깥을 누르면 닫는다
  useEffect(() => {
    if (!profileOpen) return;
    const close = (e: MouseEvent) => { if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [profileOpen]);

  // 화면이 받지 못한 실패는 공통 알림(AlertContext)이 오류 토스트로 보인다(2026-11-02 — 전에는 위 빨간 띠)

  return (
    <div className="flex h-screen bg-slate-100">
      <aside className={`${wide ? 'w-[232px]' : 'w-20'} transition-all duration-300 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col relative`}>
        {/* 펼치기 · 접기 — GRK_admin 처럼 사이드바 오른쪽 가장자리 가운데 */}
        <button type="button" onClick={() => setWide((x) => !x)} aria-label={wide ? '메뉴 접기' : '메뉴 펼치기'} aria-expanded={wide}
          className="absolute top-1/2 -translate-y-1/2 -right-3 z-50 p-1 rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-all shadow-md">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${wide ? 'rotate-180' : ''}`} aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* 로고 — GRK_project 사이드바와 같다. 「PROJECT」 자리만 「INVESTMENT」. 접으면 그림만 */}
        <div className={wide ? 'px-4 pt-5 pb-4' : 'pt-5 pb-4 flex justify-center'}>
          <Link to="/dashboard" className="flex flex-col items-center gap-1.5 group">
            <img src="/GRK_partners_세로형.jpg" alt="GRK Partners" className={`${wide ? 'h-14 w-auto' : 'h-12 w-12'} object-contain group-hover:scale-105 transition-transform`} style={{ mixBlendMode: 'multiply' }} />
            {wide && <div className="text-xs tracking-widest text-slate-400 font-medium">INVESTMENT</div>}
          </Link>
        </div>

        <nav aria-label="주 메뉴" className={`flex-1 overflow-y-auto ${wide ? 'px-3' : 'px-0'}`}>
          <div className={`flex flex-col ${wide ? 'gap-0.5' : 'items-center gap-1'}`}>
            {MENU.map((e) =>
              'group' in e ? (
                <React.Fragment key={e.group}>
                  {wide ? (
                    // 묶음 이름을 누르면 그 묶음을 접는다 — GRK_admin 「관리 영역」과 같다
                    <button type="button" onClick={() => toggleGroup(e.group)} aria-expanded={groupOpen(e.group)}
                      className="mt-2 w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-semibold tracking-wider text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
                      <ChevronRight size={14} aria-hidden="true" className={`transition-transform ${groupOpen(e.group) ? 'rotate-90' : ''}`} />
                      <span>{e.group}</span>
                    </button>
                  ) : (
                    <div className="my-1.5 h-px w-8 bg-slate-200" aria-hidden="true" />
                  )}
                  {/* 접힌 사이드바에서는 묶음과 상관없이 아이콘을 다 보인다(admin 과 같다) */}
                  {(!wide || groupOpen(e.group)) && e.items.map((m) => <Row key={m.key} m={m} on={on(m)} wide={wide} />)}
                </React.Fragment>
              ) : (
                <Row key={e.key} m={e} on={on(e)} wide={wide} />
              ),
            )}
          </div>
        </nav>

        {/* 프로필 — 모양은 GRK_admin 사이드바와 같다(아바타 40px · 이름 text-sm). 누르면 로그아웃. 접으면 아바타만 */}
        <div ref={profileRef} className={`relative mt-auto py-4 ${wide ? 'px-3' : 'flex justify-center'}`}>
          <button type="button" onClick={() => setProfileOpen((x) => !x)} aria-expanded={profileOpen} title={wide ? undefined : user?.name}
            className={`flex items-center rounded-lg hover:bg-slate-50 transition-colors ${wide ? 'gap-3 w-full px-3 py-2' : 'p-1'}`}>
            <div className={`h-10 w-10 rounded-full border-2 bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors ${profileOpen ? 'border-indigo-300' : 'border-transparent'}`}>
              <span className="text-gray-500 font-semibold text-sm">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
            </div>
            {wide && (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-500 truncate">{user?.role}</p>
              </div>
            )}
          </button>
          {profileOpen && (
            <div className={`absolute bg-white border border-slate-200 rounded-lg shadow-lg p-1 z-50 ${wide ? 'bottom-full left-3 right-3 mb-1' : 'bottom-4 left-full ml-2 w-40'}`}>
              <button type="button" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                <LogOut size={16} aria-hidden="true" /> 로그아웃
              </button>
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
};
